import 'regenerator-runtime/runtime';
import { NOT_FOUND, redirect } from 'redux-first-router';
import * as Sentry from '@sentry/browser';
import { changeCountry, changeState } from './actions/checkout';
import { fetchData, fetchCheckout } from './utils/async';
import { getCookie, setCookie, deleteAllCookies } from './utils/dom';
import { checkForLanguagePrefix } from './utils/locale';
import { deepClone } from './utils/object';

// Customer specific data is loaded in frontend.
// Generic data for all clients is SSR
const clientUpdate = (dispatch, getState) => {
	const { navigation } = getState().environment;

	if (navigation.open) dispatch({ type: 'OPEN_MENU' });
	dispatch({ type: 'CLOSE_CART' });
	dispatch({ type: 'CLEAR' });
};

const clientInit = async (dispatch, getState) => {
	clientUpdate(dispatch, getState);

	const { setup, campaign = {}, postParameters = {}, getParameters = {} } = getState().environment;
	const { selection = {}, destorySelection = false } = getState().checkout;
	const receipt = getState().location.type === 'RECEIPT';
	let selectionData = {};
	let token = null;

	const savedConsent = getCookie('cookies', true);
	if (savedConsent) {
		const consent = {};
		consent.functionality_storage = 'granted';
		consent.security_storage = 'granted';
		consent.ad_storage = savedConsent.indexOf('marketing') !== -1 || savedConsent.indexOf('all') !== -1 ? 'granted' : 'denied';
		consent.ad_user_data = savedConsent.indexOf('marketing') !== -1 || savedConsent.indexOf('all') !== -1 ? 'granted' : 'denied';
		consent.ad_personalization = savedConsent.indexOf('marketing') !== -1 || savedConsent.indexOf('all') !== -1 ? 'granted' : 'denied';
		consent.analytics_storage = savedConsent.indexOf('analytics') !== -1 || savedConsent.indexOf('all') !== -1 ? 'granted' : 'denied';
		consent.personalization_storage = 'granted';

		if (typeof window.gtag === 'function') window.gtag('consent', 'update', consent);
		if (typeof window.dataLayer === 'undefined') window.dataLayer = [];
		window.dataLayer.push({
			event: 'ConsentUpdated',
			values: {},
		});
	}

	dispatch({ type: 'LIST_ITEM_CACHE' });

	// Receipt component was not unmounted page was reloaded.
	if (destorySelection || (typeof document !== 'undefined' && getCookie('receiptShown'))) {
		dispatch({ type: 'DESTROY_SELECTION' });
		return dispatch({ type: 'START' });
	}

	if (!token && typeof localStorage !== 'undefined') {
		// transition to cookie based selection.
		token = localStorage.getItem('selection');
		if (token) {
			localStorage.removeItem('selection');
			setCookie('selection', token, 365);
		}
	}
	if (!token && selection.token) token = selection.token;
	if (!token && typeof document !== 'undefined') token = getCookie('selection');

	if (token) {
		const selectionCache = JSON.parse(localStorage.getItem('selectionCache'));
		if (selectionCache) dispatch({ type: 'INIT_SELECTION_CACHE', initCache: selectionCache });
	}

	const paymentInfo = JSON.parse(localStorage.getItem('paymentInfo'));
	if (paymentInfo) {
		dispatch({ type: 'INIT_PAYMENT_INFO', paymentInfo });
	}

	if (Object.keys(campaign).length > 0 && campaign.site) {
		selectionData = await fetchCheckout('/campaign-site', token, { uri: campaign.site }, 'PUT');

		if (selectionData?.token) {
			dispatch({ type: 'UPDATE_SELECTION', data: selectionData });
		}
	}

	if (!receipt && token) {
		// All other pages except for receipt
		selectionData = fetchCheckout('/selection', token, [], 'GET');

		try {
			selectionData = await selectionData;
		} catch (err) {
			console.log('An error occurred when trying to retrieve previous selection.', err);
			dispatch({ type: 'CHECKOUT_ERROR' });
		}

		if (selectionData.token) {
			if (setup.viewerState && setup.countries?.[setup.country]?.states?.[setup.viewerState]) {
				dispatch(changeState(setup.viewerState));
			} else if (!selectionData.location.state && setup.countries?.[setup.country]?.states) {
				dispatch(changeState(setup.country === 'US' ? 'NY' : Object.keys(setup.countries[setup.country].states)[0]));
			} else {
				dispatch({ type: 'UPDATE_SELECTION', data: selectionData });
			}
		} else {
			token = null;
		}
	} else if (receipt) {
		// Get post parameters from store.
		// Receipt page
		if (token) {
			try {
				const paymentMethodFields = { ...getParameters, ...postParameters };
				selectionData = await fetchCheckout('/payment-result', token, { paymentMethodFields }, 'POST');
			} catch (err) {
				Sentry.withScope((scope) => {
					scope.setExtras(err);
					scope.setExtras(selectionData);
					Sentry.captureMessage('RECEIPT ERROR: fetch failed, redirected to selection/failed');
					Sentry.captureException(err);
				});
				return dispatch({ type: 'SELECTION_FAILED' });
			}
		} else {
			// Token has disappeared.
			// Not much to do about it and not neccessarily wrong.
			// User may have refreshed receipt page or tried to go back but was redirected to the receipt
			Sentry.withScope((scope) => {
				scope.setExtras(selectionData);
				Sentry.captureMessage('RECEIPT WARNING: no token, redirected to start');
			});
			return dispatch({ type: 'START' });
		}

		if (selectionData.token && selectionData.order && !selectionData.errors) {
			// Purchase succeeded
			dispatch({ type: 'RECEIPT_DATA', data: selectionData });
		} else {
			// Selection errored out.
			// Need to get the original selection so the customer can try again.
			Sentry.withScope((scope) => {
				scope.setExtras(selectionData);
				Sentry.captureMessage('RECEIPT ERROR: selection had errors, redirected to selection/failed');
			});

			selectionData = fetchCheckout('/selection', token, [], 'GET');

			try {
				selectionData = await selectionData;
			} catch (err) {
				console.log('An error occurred when trying to retrieve previous selection.', err);
				dispatch({ type: 'CHECKOUT_ERROR' });
			}

			if (selectionData.token) {
				dispatch({ type: 'UPDATE_SELECTION', data: selectionData });
			}

			return dispatch({ type: 'SELECTION_FAILED' });
		}
	}

	if (window.location.search.indexOf('voucher-code') !== -1) {
		const voucher = window.location.search.split('voucher-code=')[1].split('&')[0];
		try {
			selectionData = await fetchCheckout('/vouchers', token, { voucher }, 'POST');
			if (selectionData.token) dispatch({ type: 'UPDATE_SELECTION', data: selectionData });
		} catch (err) {
			console.log('DEBUG: voucher doesn\'t exist');
		}
	}

	// If selection is set to another country than default change country data to that of website.
	if (selectionData.location && selectionData.location.country !== setup.country && setup.viewerCountry) {
		dispatch(changeCountry(setup.country));
	} else if (selectionData.location && selectionData.location.country !== setup.country && !setup.viewerCountry) {
		dispatch({ type: 'UPDATE_COUNTRY', country: selectionData.location.country, market: selectionData.location.market, pricelist: selectionData.location.pricelist });
	}

	// Ensure that we have the product cache for the current market and pricelist.
	// We load this in asyncronously because it's needed for search and will be needed for other pages if not already included.
	await retrieveProductCache(dispatch, getState);

	if (token) {
		const selectionCache = JSON.parse(localStorage.getItem('selectionCache'));
		if (selectionCache) {
			const productCache = getState().productCache[setup.market][setup.pricelist];
			const updatedProducts = Object.keys(selectionCache);
			const updatedSelectionCache = updatedProducts.reduce((acc, productKey) => ({ ...acc, [productKey]: deepClone(productCache.products[productKey]) }), {});
			dispatch({ type: 'UPDATE_SELECTION_CACHE', productCache: updatedSelectionCache, product: updatedProducts, selectionCache });
		}
	}

	let favourites = typeof localStorage !== 'undefined' ? JSON.parse(localStorage.getItem('favourites')) : false;
	if (favourites) {
		const productCache = getState().productCache[setup.market][setup.pricelist];
		favourites = favourites.filter((favourite) => productCache.products[favourite] !== undefined);
		dispatch({ type: 'UPDATE_FAVOURITES', favourites });
	}

	dispatch({ type: 'SITE_UPDATED' });

	return getState();
};

const retrieveProductCache = async (dispatch, getState) => {
	const { setup, sizeConversions } = getState().environment;
	const { previewCode, country, market, pricelist } = setup;
	let productCache;

	if (getState().productCache[market] && getState().productCache[market][pricelist]) return getState().productCache[market][pricelist];

	try {
		const body = previewCode ? { previewCode } : {};
		productCache = await fetchData(`product-cache/${market}/${pricelist}/`, body);
	} catch (err) {
		console.log('DEBUG: product cache not loaded');
	}

	dispatch({ type: 'PRODUCT_CACHE', market, pricelist, setup, productCache, sizeConversions });
	return dispatch({ type: 'UPDATE_COUNTRY', market, pricelist, country });
};

const languagePrefix = ''; // '/:language?';
const useLanguage = (str = '') => `${languagePrefix}${str}`;

export default {
	CLIENT_INIT: {
		thunk: async (dispatch, getState) => clientInit(dispatch, getState).catch((err) => {
			Sentry.captureException(err);
			console.log('DEBUG: error initiating client site data', err);
		}),
	},
	RETRIEVE_PRODUCT_CACHE: {
		thunk: async (dispatch, getState) => (
			retrieveProductCache(dispatch, getState)
		),
	},
	STATIC: {
		path: '/static/:noop?',
	},
	REFERRAL: {
		path: '/ref',
		thunk: async (dispatch, getState, bag) => {
			const { location, environment } = getState();
			const { getParameters = {}, setup } = environment;
			const { hash = '' } = bag.action.payload;
			let path = '';
			let search = '';
			const goTo = Object.keys(getParameters).reduce((acc, param) => (param.toLowerCase() === 'goto' ? getParameters[param] : acc), '');
			if (goTo) path = decodeURIComponent(goTo).replace(/^\/+/, '');
			if (path.indexOf('#') !== -1) path = path.slice(0, path.indexOf('#'));
			if (path.indexOf('?') !== -1) {
				search = path.slice(path.indexOf('?'));
				path = path.slice(0, path.indexOf('?'));
			} else if (location.search) {
				search = `?${location.search}`;
			}
			if (path === '/' || path === '') return dispatch(redirect({ type: 'START', payload: { language: setup.language, path: '', search, hash } }));
			return dispatch(redirect({ type: 'CATEGORY_PRODUCT_PAGE', payload: { language: setup.language, path, search, hash } }));
		},
	},
	CLEAR_COOKIES: {
		path: useLanguage('/clear-cookies'),
		thunk: async (dispatch) => {
			deleteAllCookies();
			return dispatch(redirect({ type: 'START' }));
		},
	},
	START: {
		path: '/',
		thunk: async (dispatch, getState, bag) => {
			dispatch({ type: 'PAGE_LOADING', initiated: Date.now() });
			clientUpdate(dispatch, getState);

			const { setup, restrictedPages, campaignSite = false } = getState().environment;
			const { languages, previewCode } = setup;
			const path = decodeURIComponent(getState().location.payload.path).replace(/^\/+/, '');
			const paths = languages.length > 0 ? Object.keys(languages).reduce((acc, pathLanguage) => ({ ...acc, [pathLanguage]: '' }), {}) : path;

			const body = previewCode ? { previewCode } : {};
			const isPageRestricted = restrictedPages.filter((rP) => rP[(setup?.language || 'en')] === path);
			if (campaignSite && isPageRestricted.length > 0 && setup?.cookies?.campaign) {
				body.restrictedCode = campaignSite;
				body.restrictedId = setup.campaignType;
			}
			const startPage = !getState().pageData || !getState().pageData.start ? fetchData('', body) : getState().pageData.start;
			let startPageData;

			let { hash = '' } = bag.action.payload;
			hash = typeof window !== 'undefined' && window.location.hash !== '' && window.location.hash !== '#' ? window.location.hash : hash;
			dispatch({ type: 'UPDATE_HASH', hash });

			try {
				startPageData = await startPage;
			} catch (err) {
				console.log('DEBUG: start page data not retrieved');
			}

			dispatch({ type: 'PAGE_DATA', data: startPageData, page: 'start' });

			if (startPageData?.content?.pageHasProducts) await retrieveProductCache(dispatch, getState);
			return dispatch({ type: 'PAGE_LOADED', paths, pageType: 'start' });
		},
	},
	SELECTION: {
		path: useLanguage('/selection'),
		thunk: async (dispatch, getState) => {
			dispatch({ type: 'PAGE_LOADING', initiated: Date.now() });
			clientUpdate(dispatch, getState);

			const { languages, market, pricelist, country } = getState().environment.setup;
			const paths = Object.keys(languages).reduce((acc, pathLanguage) => ({ ...acc, [pathLanguage]: 'selection' }), {});

			const body = getState().environment.setup.previewCode ? { previewCode: getState().environment.setup.previewCode } : {};
			const selectionPage = !getState().pageData || !getState().pageData.selection ? fetchData('selection', body) : getState().pageData.selection;
			let selectionPageData;

			try {
				selectionPageData = await selectionPage;
			} catch (err) {
				console.log('DEBUG: failed page data not retrieved');
			}

			dispatch({ type: 'PAGE_DATA', data: selectionPageData, page: 'selection' });

			if (selectionPageData?.content?.pageHasProducts) await retrieveProductCache(dispatch, getState, market, pricelist, country);
			return dispatch({ type: 'PAGE_LOADED', paths, pageType: 'selection' });
		},
	},
	SELECTION_FAILED: {
		path: useLanguage('/selection/failed'),
		thunk: async (dispatch, getState) => {
			dispatch({ type: 'PAGE_LOADING', initiated: Date.now() });
			clientUpdate(dispatch, getState);

			const { languages } = getState().environment.setup;
			const paths = Object.keys(languages).reduce((acc, pathLanguage) => ({ ...acc, [pathLanguage]: 'selection/failed' }), {});
			let failedPageData;

			const body = getState().environment.setup.previewCode ? { previewCode: getState().environment.setup.previewCode } : {};
			const failedPage = !getState().pageData || !getState().pageData['selection/failed'] ? fetchData('selection/failed', body) : getState().pageData['selection/failed'];

			try {
				failedPageData = await failedPage;
			} catch (err) {
				console.log('DEBUG: failed page data not retrieved');
			}

			dispatch({ type: 'PAGE_DATA', data: failedPageData, page: 'selection/failed' });
			return dispatch({ type: 'PAGE_LOADED', paths, pageType: 'selection/failed' });
		},
	},
	RECEIPT: {
		path: useLanguage('/selection/receipt'),
		thunk: async (dispatch, getState) => {
			dispatch({ type: 'PAGE_LOADING', initiated: Date.now() });
			clientUpdate(dispatch, getState);

			const { languages } = getState().environment.setup;
			const paths = Object.keys(languages).reduce((acc, pathLanguage) => ({ ...acc, [pathLanguage]: 'selection/receipt' }), {});

			return dispatch({ type: 'PAGE_LOADED', paths, pageType: 'selection/receipt' });
		},
	},
	RECEIPT_DEBUG: {
		path: useLanguage('/selection/receipt/debug'),
		thunk: async (dispatch, getState) => {
			dispatch({ type: 'PAGE_LOADING', initiated: Date.now() });
			clientUpdate(dispatch, getState);

			const { languages } = getState().environment.setup;
			const paths = Object.keys(languages).reduce((acc, pathLanguage) => ({ ...acc, [pathLanguage]: 'selection/receipt/debug' }), {});

			return dispatch({ type: 'PAGE_LOADED', paths, pageType: 'selection/receipt/debug' });
		},
	},
	SELECTION_RECOVER: {
		path: useLanguage('/selection/recover/:newToken*'),
		thunk: async (dispatch, getState) => {
			const { location, environment } = getState();
			const { newToken = '' } = location.payload;
			let { language = false } = location.payload;
			if (!language) language = environment.setup.language;

			if (!newToken) {
				return dispatch(redirect({ type: 'START', payload: { language } }));
			}

			try {
				const selectionData = await fetchCheckout(`/selection/${newToken}`, false, {}, 'PUT');
				if (selectionData.token) {
					dispatch({ type: 'UPDATE_SELECTION', data: selectionData });
					const payload = language === 'en' ? {} : { language };
					return dispatch(redirect({ type: 'SELECTION', payload }));
				}
				return dispatch(redirect({ type: 'START', payload: { language } }));
			} catch (err) {
				return dispatch(redirect({ type: 'START', payload: { language } }));
			}
		},
	},
	SELECTION_PAYMENT_LINK: {
		path: useLanguage('/selection/:paymentLink*'),
		thunk: async (dispatch, getState) => {
			const { location, environment } = getState();
			const { selection = {} } = getState().checkout;
			const { paymentLink = '' } = location.payload;
			let { language = false } = location.payload;
			if (!language) language = environment.setup.language;

			let token = null;
			if (!token && selection.token) token = selection.token;
			if (!token && typeof document !== 'undefined') token = getCookie('selection');

			if (!paymentLink) {
				return dispatch(redirect({ type: 'START', payload: { language } }));
			}

			try {
				const paymentLinkResult = await fetchData('payment-links', { paymentLink });
				if (paymentLinkResult?.success && paymentLinkResult?.data) {
					const selectionData = await fetchCheckout(`/selection/${paymentLinkResult.data}`, token, {}, 'PUT');
					if (selectionData.token) {
						dispatch({ type: 'UPDATE_SELECTION', data: selectionData });
						const payload = language === 'en' ? {} : { language };
						return dispatch(redirect({ type: 'SELECTION', payload }));
					}
				}
				return dispatch(redirect({ type: 'START', payload: { language } }));
			} catch (err) {
				return dispatch(redirect({ type: 'START', payload: { language } }));
			}
		},
	},
	CATEGORY_PRODUCT_PAGE: {
		path: useLanguage('/:path*'),
		thunk: async (dispatch, getState, bag) => {
			dispatch({ type: 'PAGE_LOADING', initiated: Date.now() });
			clientUpdate(dispatch, getState);
			/*
				USE CASES:
				/women/clothing/dresses/harper - Product
				/women/clothing/harper - Product
				/women/clothing - Category
				/en/women/clothing - Category with localization
				/sv/dam/kläder - Category with right localization but wrong locale - redirect to english version.
				/en/page
				/page
			*/
			const { location, environment, pageData = {} } = getState();
			const { setup, restrictedPages, campaignSite } = environment;
			const { hash = '' } = bag.action.payload;
			let { language = false, path = '' } = location.payload;
			// Clean any encoded URI characters
			let search = '';
			path = decodeURIComponent(path).replace(/^\/+/, '');

			// Remove any hashes from path
			if (path.indexOf('#') !== -1) path = path.slice(0, path.indexOf('#'));
			// Remove any queries from path
			if (path.indexOf('?') !== -1) {
				search = path.slice(path.indexOf('?'));
				path = path.slice(0, path.indexOf('?'));
			} else if (location.search) {
				search = `?${location.search}`;
			}
			// "language" is probably not a language but a path.
			if (language && !checkForLanguagePrefix(language)) {
				// Join language and path together.
				path = `${language}${path.length > 0 ? `/${path}` : ''}`;
				// Set language to correct language
				language = environment.setup.language;
			}

			// Get content for site.
			const body = setup.previewCode ? { previewCode: setup.previewCode } : {};
			const isPageRestricted = restrictedPages.filter((rP) => rP.en === path);
			if (campaignSite && isPageRestricted.length > 0 && setup?.cookies?.campaign) {
				body.restrictedCode = campaignSite;
				body.restrictedId = setup.campaignType;
			}

			const page = pageData[path || 'start'] || fetchData(path, body);
			let pageDataReturn = {};
			clientUpdate(dispatch, getState);

			if (path === '') path = 'start';

			try {
				[, pageDataReturn] = await Promise.all([retrieveProductCache(dispatch, getState), page]);
			} catch (err) {
				console.log(`DEBUG: failed page data not retrieved. ${path}`, err);
			}

			if (pageDataReturn && pageDataReturn.is404) {
				dispatch({ type: NOT_FOUND });
				return dispatch({ type: 'PAGE_LOADED', paths: path, pageType: '404' });
			}

			if (pageDataReturn.paths) {
				if (language && path !== pageDataReturn.paths[language] && !pageDataReturn.content.is404 && pageDataReturn.paths[language] && pageDataReturn.paths[language] !== 'start') {
					return dispatch(redirect({ type: 'CATEGORY_PRODUCT_PAGE', payload: { language, path: pageDataReturn.paths[language], search, hash } }));
				}
			}

			dispatch({ type: 'PAGE_DATA', data: pageDataReturn, page: path });

			const currentHash = typeof window !== 'undefined' && window.location.hash !== '' && window.location.hash !== '#' ? window.location.hash : hash;

			if (['category'].indexOf(pageDataReturn.pageType) !== -1) {
				const hashFilter = [];
				const filterPath = pageDataReturn.pageType === 'product' ? path.slice(0, path.lastIndexOf('/')) : path;

				if (currentHash) {
					dispatch({ type: 'UPDATE_HASH', hash: currentHash });
					currentHash.replace(/^[?#]/, '').split('&').forEach((filter) => {
						const filterParts = filter.split('=');
						if (filterParts.length === 1) {
							hashFilter.push({ [filterParts[0]]: true });
						} else {
							hashFilter.push({ [filterParts[0]]: filterParts[1].split(',') });
						}
					});
				}

				dispatch({ type: 'RESET_FILTER' });
				dispatch({ type: 'FILTER', filterArray: [{ categories: filterPath }, ...hashFilter], force: true, skipUrlUpdate: true, setup: environment.setup, currentPath: filterPath });
			}

			return dispatch({ type: 'PAGE_LOADED', paths: pageDataReturn.paths || path, pageType: pageDataReturn.pageType || 'page' });
		},
	},
};
